import { useToast } from '@app/core/notifications';
import { useOwnedStations } from '@app/core/stations/hooks/useOwnedStations';
import { contentAi } from '@mi-front/api-clients';
import { slugify } from '@mi-front/core';
import { Form, NullMessage, TextAreaField, TextField } from '@mi-front/ui';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { Box, Button, Grid, Paper } from '@mui/material';
import { FieldArray } from 'formik';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import * as yup from 'yup';
import { StationsInput } from '../../../../../components/stations/StationsInput';

export function StationConfigForm() {
  const { $t } = useIntl();
  const { notifySuccess } = useToast();
  const stations = useOwnedStations();
  const [station, setStation] = useState(stations[0]);

  const mutation = contentAi.useStationConfigUpdate();
  const config = contentAi.useStationConfigGet(station?.id);

  return (
    <Box>
      <Grid container>
        <Grid item md={5}>
          <StationsInput stations={stations} value={station} onChange={setStation} />
        </Grid>
      </Grid>

      {config.data && station && (
        <Box>
          <Form
            key={station.id}
            validationSchema={yup.object().shape({
              prompts: yup.array(
                yup.object().shape({
                  name: yup.string().required(
                    $t({
                      id: 'common.required',
                      defaultMessage: 'Requerido',
                    }),
                  ),
                  content: yup.string().required(
                    $t({
                      id: 'common.required',
                      defaultMessage: 'Requerido',
                    }),
                  ),
                }),
              ),
            })}
            initialValues={{
              prompts: config.data?.prompts ?? [],
            }}
            onSubmit={async (data) => {
              await mutation.mutateAsync({
                stationId: station.id,
                data: {
                  prompts: data.prompts.map((p) => ({
                    ...p,
                    id: p.id?.trim() || slugify(p.name).toUpperCase(),
                  })),
                },
              });

              notifySuccess(
                $t(
                  {
                    id: 'contentAi.stationConfig.updated',
                    defaultMessage: 'Content AI config for {stationName} updated',
                  },
                  { stationName: station.name },
                ),
              );
            }}
          >
            {({ values, errors }) => {
              return (
                <Box mb={4}>
                  <h6>
                    <FormattedMessage
                      id="contentAi.stationConfig.promptsTitle"
                      defaultMessage="Prompts"
                    />
                  </h6>
                  <FieldArray
                    name="prompts"
                    render={(arrayHelpers) => (
                      <div>
                        {!values.prompts?.length && (
                          <NullMessage>
                            <FormattedMessage
                              id="stationConfig.noPrompts"
                              defaultMessage="No hay prompts registrados"
                            />
                          </NullMessage>
                        )}
                        {values.prompts?.map((prompt, index) => (
                          <div key={index}>
                            <Paper>
                              <Box mb={2} p={2} display={'flex'}>
                                <Box mr={2}>{index + 1}.</Box>
                                <Box flexGrow={1}>
                                  <Box>
                                    <Grid container>
                                      <Grid item md={5}>
                                        <TextField
                                          label={$t({
                                            id: 'common.name',
                                            defaultMessage: 'Nombre',
                                          })}
                                          name={`prompts.${index}.name`}
                                          markRequired
                                        />
                                      </Grid>
                                      <Grid item md={7} textAlign={'right'}>
                                        <Button
                                          variant="outlined"
                                          color="error"
                                          size="small"
                                          startIcon={<DeleteIcon />}
                                          onClick={() => arrayHelpers.remove(index)}
                                        >
                                          {$t({
                                            id: 'common.delete',
                                            defaultMessage: 'Eliminar',
                                          })}
                                        </Button>
                                      </Grid>
                                    </Grid>
                                    <Grid container>
                                      <Grid item md={12}>
                                        <TextAreaField
                                          label={$t({
                                            id: 'common.text.content',
                                            defaultMessage: 'Contenido',
                                          })}
                                          name={`prompts.${index}.content`}
                                          rows={4}
                                          markRequired
                                        />
                                      </Grid>
                                    </Grid>
                                  </Box>
                                </Box>
                              </Box>
                            </Paper>
                          </div>
                        ))}

                        <Box mt={3}>
                          <Button
                            variant="outlined"
                            color="secondary"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={() =>
                              arrayHelpers.insert(values.prompts.length, {
                                id: '',
                                name: '',
                                content: '',
                              })
                            }
                          >
                            {$t({
                              id: 'contentAi.stationConfig.addPrompt',
                              defaultMessage: 'Add new prompt',
                            })}
                          </Button>
                        </Box>
                      </div>
                    )}
                  />
                </Box>
              );
            }}
          </Form>
        </Box>
      )}
    </Box>
  );
}
